/* DASH-CUSTOM-06B — DASHBOARD LAYOUT CONFIG V2 (FOUNDATION, NON ATTIVA)
 * ============================================================================
 * Schema V2 del layout della Dashboard Manager: `order` piatta + `enabled` +
 * `span` per widget. Funzioni PURE e TESTABILI: nessun accesso a rete, storage,
 * DOM o orologio. Solo trasformazioni deterministiche.
 *
 * PERCHE' UN MODULO SEPARATO (decisione architetturale 06B):
 *   il normalizer V1 (dashboardLayoutConfig.js) e' la versione ATTIVA usata dal
 *   runtime Stage 05 e dal provider PHP (schema 1). NON va cambiato: sarebbe un
 *   breaking change contro un backend che accetta ancora solo V1. La V2 vive qui
 *   come foundation isolata, pronta per 06C (provider PHP) e 06D (renderer).
 *   Nessun file del runtime importa questo modulo: la DashboardManager continua
 *   a usare esclusivamente la V1.
 *
 * FORMATO CANONICO V2
 * ---------------------------------------------------------------------------
 *   { "version": 2,
 *     "order":   ["welcome", "kpi", ... ],
 *     "widgets": { "welcome": { "enabled": true },
 *                  "kpi":     { "enabled": true, "span": 4,
 *                                 "position": { "zone": "left", "row": 0, "rank": 0 } },
 *                  ... } }
 *
 * `position` e' opzionale: solo i widget personalizzabili (span-capable).
 * welcome e' SYSTEM UI (non ha span, non riceve position).
 *
 * `span` e' una PREFERENZA PERSONALE (intero, solo larghezza). Le capability
 * geometriche (defaultSpan/minSpan/maxSpan/resizable) NON sono duplicate qui:
 * vivono nel registry (widgetRegistry.js), fonte unica.
 *
 * WELCOME (SYSTEM UI): resta nel contratto per compatibilita' (provider PHP
 * Stage 05 lo riconosce), ma NON riceve `span` e non e' personalizzabile.
 * Un widget e' "span-capable" se e solo se il registry dichiara un defaultSpan
 * numerico finito.
 *
 * FONTE UNICA (source of truth):
 *   - ORDER default   -> dashboardDefaults.js (DEFAULT_MANAGER_V1_ORDER)
 *   - ENABLED default -> dashboardDefaults.js (getDefaultForRole)
 *   - SPAN default/min/max/resizable -> widgetRegistry.js (capability)
 * ============================================================================
 */

import { WIDGET_REGISTRY_V1, DASHBOARD_LAYOUT_SCHEMA_VERSION_V2 } from './widgetRegistry';
import { getDefaultForRole, DEFAULT_ROLE_MANAGER } from './dashboardDefaults';
import { MAX_LAYOUT_PAYLOAD_CHARS } from './dashboardLayoutConfig';

/* Limiti ASSOLUTI della griglia (12 colonne desktop). I limiti effettivi per
 * widget sono minSpan/maxSpan del registry, sempre dentro questo intervallo. */
export const SPAN_ABS_MIN = 1;
export const SPAN_ABS_MAX = 12;

/* Esiti di migrazione V2 (self-contained: la V2 non dipende dal modulo V1). */
export const MIGRATION_OUTCOME_V2 = Object.freeze({
    IDENTITY: 'IDENTITY',
    MIGRATED: 'MIGRATED',
    UNSUPPORTED_FUTURE_VERSION: 'UNSUPPORTED_FUTURE_VERSION',
    UNKNOWN_VERSION: 'UNKNOWN_VERSION',
});

function isPlainObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isStrictBoolean(v) {
    return typeof v === 'boolean';
}

/* ==========================================================================
 * POSITION (opzionale) — vincoli documentati
 * ==========================================================================
 * zone ammesse: solo 'left', 'right', 'free' (case-insensitive in ingresso,
 * normalizzata a minuscolo). row/rank: interi non negativi, con un limite
 * superiore documentato (POSITION_MAX_COORD) per evitare payload assurdi.
 */
const POSITION_ALLOWED_ZONES = ['left', 'right', 'free'];
const POSITION_MAX_COORD = 9999;
const POSITION_MAX_ROW = 9999;
const POSITION_MAX_COLUMN = 11;
const POSITION_MAX_ROWSPAN = 12;
const POSITION_GRID_KEYS = ['row', 'column', 'rowSpan'];
const POSITION_LEGACY_KEYS = ['zone', 'row', 'rank'];

function isStrictIntegerInRange(v, min, max) {
    return typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
}

function isStrictNonNegativeInteger(v) {
    return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= POSITION_MAX_COORD;
}

function readPosition(raw) {
    const pos = isPlainObject(raw) ? raw : null;
    const empty = { ok: true, kind: null, grid: null, legacy: null };
    if (!pos) return empty;

    const keys = Object.keys(pos);
    const hasGrid = keys.indexOf('column') !== -1 || keys.indexOf('rowSpan') !== -1;
    const hasLegacy = keys.indexOf('zone') !== -1 || keys.indexOf('rank') !== -1;

    if (hasGrid && hasLegacy) {
        return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_MIXED_FORMAT' };
    }

    if (hasGrid) {
        const extra = keys.filter((k) => POSITION_GRID_KEYS.indexOf(k) === -1);
        if (extra.length > 0) {
            return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_EXTRA_KEY' };
        }
        const grid = { row: null, column: null, rowSpan: null };
        if (Object.prototype.hasOwnProperty.call(pos, 'row') && pos.row !== null) {
            if (!isStrictIntegerInRange(pos.row, 0, POSITION_MAX_ROW)) {
                return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_ROW_INVALID' };
            }
            grid.row = pos.row;
        }
        if (Object.prototype.hasOwnProperty.call(pos, 'column') && pos.column !== null) {
            if (!isStrictIntegerInRange(pos.column, 0, POSITION_MAX_COLUMN)) {
                return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_COLUMN_INVALID' };
            }
            grid.column = pos.column;
        }
        if (Object.prototype.hasOwnProperty.call(pos, 'rowSpan') && pos.rowSpan !== null) {
            if (!isStrictIntegerInRange(pos.rowSpan, 1, POSITION_MAX_ROWSPAN)) {
                return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_ROWSPAN_INVALID' };
            }
            grid.rowSpan = pos.rowSpan;
        }
        if (grid.row === null && grid.column === null && grid.rowSpan === null) return empty;
        return { ok: true, kind: 'grid', grid, legacy: null };
    }

    const extra = keys.filter((k) => POSITION_LEGACY_KEYS.indexOf(k) === -1);
    if (extra.length > 0) {
        return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_EXTRA_KEY' };
    }
    const legacy = { zone: null, row: null, rank: null };
    if (Object.prototype.hasOwnProperty.call(pos, 'zone') && pos.zone !== null) {
        const z = typeof pos.zone === 'string' ? pos.zone.toLowerCase() : '';
        if (POSITION_ALLOWED_ZONES.indexOf(z) === -1) {
            return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_ZONE_INVALID' };
        }
        legacy.zone = z;
    }
    if (Object.prototype.hasOwnProperty.call(pos, 'row') && pos.row !== null) {
        if (!isStrictNonNegativeInteger(pos.row)) {
            return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_ROW_INVALID' };
        }
        legacy.row = pos.row;
    }
    if (Object.prototype.hasOwnProperty.call(pos, 'rank') && pos.rank !== null) {
        if (!isStrictNonNegativeInteger(pos.rank)) {
            return { ok: false, kind: null, grid: null, legacy: null, reason: 'POSITION_RANK_INVALID' };
        }
        legacy.rank = pos.rank;
    }
    if (legacy.zone === null && legacy.row === null && legacy.rank === null) return empty;
    return { ok: true, kind: 'legacy', grid: null, legacy };
}

export function projectZonePositionToGrid(position) {
    const pos = isPlainObject(position) ? position : null;
    if (!pos) return null;
    const zone = typeof pos.zone === 'string' ? pos.zone.toLowerCase() : '';
    const row = isStrictNonNegativeInteger(pos.row) ? pos.row : 0;
    const rank = isStrictNonNegativeInteger(pos.rank) ? pos.rank : 0;
    if (zone === 'left') return { row, column: 0, rowSpan: 1 };
    if (zone === 'right') return { row, column: rank === 1 ? 9 : 5, rowSpan: 1 };
    if (zone === 'free') return { row, column: 0, rowSpan: 1 };
    return null;
}

function gridCellsOverlap(pa, spanA, pb, spanB) {
    const ra = pa.row, ha = pa.rowSpan || 1;
    const rb = pb.row, hb = pb.rowSpan || 1;
    const ca = pa.column, wa = spanA;
    const cb = pb.column, wb = spanB;
    const rowOverlap = ra < rb + hb && rb < ra + ha;
    const colOverlap = ca < cb + wb && cb < ca + wa;
    return rowOverlap && colOverlap;
}

export function detectGridCollisions(entries) {
    const list = Array.isArray(entries) ? entries : [];
    const out = [];
    for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
            const a = list[i], b = list[j];
            if (!a || !b || !a.position || !b.position) continue;
            if (gridCellsOverlap(a.position, a.span, b.position, b.span)) {
                out.push([a.key, b.key]);
            }
        }
    }
    return out;
}

export function projectLayoutZoneToGrid(config, opts) {
    const o = isPlainObject(opts) ? opts : {};
    const registry = o.registry || WIDGET_REGISTRY_V1;
    const src = isPlainObject(config)
        ? config
        : { version: DASHBOARD_LAYOUT_SCHEMA_VERSION_V2, order: [], widgets: {} };
    const order = Array.isArray(src.order) ? src.order.slice() : [];
    const srcWidgets = isPlainObject(src.widgets) ? src.widgets : {};

    const projected = Object.create(null);
    let leftMax = -1;
    let rightMax = -1;

    order.forEach((k) => {
        const w = isPlainObject(srcWidgets[k]) ? srcWidgets[k] : {};
        const pos = isPlainObject(w.position) ? w.position : null;
        if (!pos) { projected[k] = null; return; }
        if (Object.prototype.hasOwnProperty.call(pos, 'column') || Object.prototype.hasOwnProperty.call(pos, 'rowSpan')) {
            projected[k] = {
                row: isStrictIntegerInRange(pos.row, 0, POSITION_MAX_ROW) ? pos.row : 0,
                column: isStrictIntegerInRange(pos.column, 0, POSITION_MAX_COLUMN) ? pos.column : 0,
                rowSpan: isStrictIntegerInRange(pos.rowSpan, 1, POSITION_MAX_ROWSPAN) ? pos.rowSpan : 1,
            };
            return;
        }
        const g = projectZonePositionToGrid(pos);
        projected[k] = g;
        if (g) {
            const zone = typeof pos.zone === 'string' ? pos.zone.toLowerCase() : '';
            if (zone === 'left') leftMax = Math.max(leftMax, g.row);
            else if (zone === 'right') rightMax = Math.max(rightMax, g.row);
        }
    });

    const freeOffset = Math.max(leftMax, rightMax) + 1;
    order.forEach((k) => {
        const w = isPlainObject(srcWidgets[k]) ? srcWidgets[k] : {};
        const pos = isPlainObject(w.position) ? w.position : null;
        if (pos && typeof pos.zone === 'string' && pos.zone.toLowerCase() === 'free' && projected[k]) {
            projected[k] = { row: freeOffset + projected[k].row, column: projected[k].column, rowSpan: projected[k].rowSpan };
        }
    });

    const entries = [];
    order.forEach((k) => {
        if (!projected[k]) return;
        const w = isPlainObject(srcWidgets[k]) ? srcWidgets[k] : {};
        const g = registry.geometry(k);
        const span = isFiniteNumber(w.span) ? w.span : (g && isFiniteNumber(g.defaultSpan) ? g.defaultSpan : SPAN_ABS_MAX);
        entries.push({ key: k, position: projected[k], span });
    });
    const collisions = detectGridCollisions(entries);
    if (collisions.length > 0) {
        return { ok: false, reason: 'PROJECTION_COLLISION', collisions, config: src };
    }

    const widgets = {};
    order.forEach((k) => {
        const w = isPlainObject(srcWidgets[k]) ? srcWidgets[k] : {};
        const entry = { enabled: w.enabled === true };
        if (isFiniteNumber(w.span)) entry.span = Math.round(w.span);
        if (projected[k]) {
            entry.position = { row: projected[k].row, column: projected[k].column, rowSpan: projected[k].rowSpan };
        }
        widgets[k] = entry;
    });
    return { ok: true, config: { version: DASHBOARD_LAYOUT_SCHEMA_VERSION_V2, order, widgets } };
}

function isFiniteNumber(v) {
    return typeof v === 'number' && Number.isFinite(v);
}
/* ==========================================================================
 * SPAN NORMALIZATION (regola UNIVOCA)
 * ==========================================================================
 * Un solo punto di verita' per trasformare un valore grezzo in uno span valido.
 *
 * REGOLE (documentate, deterministiche):
 *   1. widget non span-capable -> nessuno span (value null, reason null);
 *   2. valore NON numero finito (stringa, boolean, NaN, Infinity, null,
 *      undefined, oggetto/array) -> defaultSpan (SPAN_FALLBACK);
 *   3. numero finito -> arrotondamento all'intero piu' vicino (Math.round);
 *      la conversione e' ESPLICITA e documentata: 3.4 -> 3, 3.5 -> 4, 11.9 -> 12;
 *   4. clamp a [max(minSpan,1), min(maxSpan,12)]: sotto -> SPAN_CLAMPED_MIN,
 *      sopra -> SPAN_CLAMPED_MAX. Copre 0, negativi e valori > 12.
 * L'output e' SEMPRE un intero valido quando il widget e' span-capable.
 *
 * @returns {{value:number|null, reason:string|null}}
 */
export function normalizeSpan(raw, geometry) {
    if (!geometry || !isFiniteNumber(geometry.defaultSpan)) {
        return { value: null, reason: null };
    }
    if (!isFiniteNumber(raw)) {
        return { value: geometry.defaultSpan, reason: 'SPAN_FALLBACK' };
    }
    /* Math.round di un numero finito e' sempre finito: nessun guard ulteriore. */
    let value = Math.round(raw);
    let reason = null;
    const lo = Math.max(SPAN_ABS_MIN, isFiniteNumber(geometry.minSpan) ? geometry.minSpan : SPAN_ABS_MIN);
    const hi = Math.min(SPAN_ABS_MAX, isFiniteNumber(geometry.maxSpan) ? geometry.maxSpan : SPAN_ABS_MAX);
    if (value < lo) { value = lo; reason = 'SPAN_CLAMPED_MIN'; }
    else if (value > hi) { value = hi; reason = 'SPAN_CLAMPED_MAX'; }
    return { value, reason };
}

/* ==========================================================================
 * SERIALIZZAZIONE CANONICA V2
 * ==========================================================================
 * Deterministica: chiavi in ordine fisso, `enabled` prima di `span`. Serve per
 * i test di idempotenza/migrazione. Non dipende dall'ordine di JSON.stringify.
 */
export function serializeCanonicalV2(config) {
    const c = isPlainObject(config) ? config : {};
    const order = Array.isArray(c.order) ? c.order : [];
    const widgets = isPlainObject(c.widgets) ? c.widgets : {};
    const parts = order.map((k) => {
        const w = isPlainObject(widgets[k]) ? widgets[k] : {};
        const enabled = w.enabled === true ? 'true' : 'false';
        const hasPosition = Object.prototype.hasOwnProperty.call(w, 'position');
        const positionPart = hasPosition
            ? ',"position":' + JSON.stringify(w.position)
            : '';
        if (isFiniteNumber(w.span)) {
            return JSON.stringify(k) + ':{"enabled":' + enabled + ',"span":' + Math.round(w.span) + positionPart + '}';
        }
        return JSON.stringify(k) + ':{"enabled":' + enabled + positionPart + '}';
    }).join(',');
    return '{"version":' + DASHBOARD_LAYOUT_SCHEMA_VERSION_V2
        + ',"order":[' + order.map((k) => JSON.stringify(String(k))).join(',') + ']'
        + ',"widgets":{' + parts + '}}';
}
/* ==========================================================================
 * MIGRAZIONE V1 -> V2 (funzione PURA, non-mutante)
 * ==========================================================================
 * Input: config V1 valida `{version:1, order:[...], widgets:{k:{enabled}}}`.
 * Output: bozza V2 `{version:2, order, widgets:{k:{enabled, span?}}}`.
 *
 * REGOLE:
 *   - NON muta l'input (slice/copia);
 *   - `span` = registry.defaultSpan SOLO per i widget span-capable
 *     (welcome NON lo riceve);
 *   - l'ordine V1 viene preservato (la canonizzazione lo ripulisce dopo);
 *   - widget senza geometria (welcome) -> solo `enabled`;
 *   - il risultato NON e' ancora canonico (unknown/duplicati/mancanti sono
 *     risolti dalla normalizzazione successiva) => migrazione idempotente
 *     RISPETTO a normalizeDashboardLayoutV2.
 *
 * @returns {{outcome:string, config:Object|null}}
 */
export function migrateV1ToV2(config, opts) {
    const registry = (opts && opts.registry) || WIDGET_REGISTRY_V1;
    if (!isPlainObject(config) || config.version !== 1) {
        return { outcome: MIGRATION_OUTCOME_V2.IDENTITY, config: isPlainObject(config) ? config : null };
    }
    const order = Array.isArray(config.order) ? config.order.slice() : [];
    const srcWidgets = isPlainObject(config.widgets) ? config.widgets : {};
    const widgets = {};
    Object.keys(srcWidgets).forEach((k) => {
        const entry = srcWidgets[k];
        const enabled = isPlainObject(entry) && isStrictBoolean(entry.enabled) ? entry.enabled : true;
        const geometry = registry.geometry(k);
        const out = { enabled };
        if (geometry && isFiniteNumber(geometry.defaultSpan)) out.span = geometry.defaultSpan;
        widgets[k] = out;
    });
    return {
        outcome: MIGRATION_OUTCOME_V2.MIGRATED,
        config: { version: DASHBOARD_LAYOUT_SCHEMA_VERSION_V2, order, widgets },
    };
}

/* ==========================================================================
 * DEFAULT V2
 * ==========================================================================
 * Derivato dalle fonti uniche (dashboardDefaults + registry): nessuna
 * duplicazione manuale. Coincide con normalizeDashboardLayoutV2({rawConfig:null}).
 */
export function buildDefaultV2(opts) {
    const o = isPlainObject(opts) ? opts : {};
    return normalizeDashboardLayoutV2({
        registry: o.registry,
        role: o.role,
        defaultConfig: o.defaultConfig,
        authorizedKeys: o.authorizedKeys,
        rawConfig: null,
    }).config;
}
/* ==========================================================================
 * NORMALIZER V2
 * ==========================================================================
 * Principio: DEFAULT -> overlay personale valido -> sanitizzazione -> authorization
 * -> config canonica V2. Funzione TOTALE: non lancia mai. Deterministica: stesso
 * input => stesso output. NON muta l'input.
 *
 * @returns {{config:Object, authorized:Array, dropped:Array, added:Array,
 *            migration:string, reasons:Array}}
 */
export function normalizeDashboardLayoutV2(opts) {
    const options = isPlainObject(opts) ? opts : {};
    const registry = options.registry || WIDGET_REGISTRY_V1;
    const role = (typeof options.role === 'string' && options.role) ? options.role : DEFAULT_ROLE_MANAGER;
    const defaultConfig = (isPlainObject(options.defaultConfig) ? options.defaultConfig : null)
        || getDefaultForRole(role)
        || { version: 1, order: [], widgets: {} };

    const reasons = [];
    const dropped = [];
    const added = [];

    /* ---- 0. chiavi valide del default: fonte dell'ordine di default ---- */
    const defaultKeys = [];
    if (Array.isArray(defaultConfig.order)) {
        defaultConfig.order.forEach((k) => {
            if (registry.has(k) && defaultKeys.indexOf(k) === -1) defaultKeys.push(k);
        });
    }
    if (defaultKeys.length === 0) {
        registry.keys().forEach((k) => defaultKeys.push(k));
        reasons.push('DEFAULT_EMPTY');
    }

    /* ---- 1. overlay personale grezzo, parsing difensivo ---- */
    let raw = options.rawConfig;
    if (typeof raw === 'string') {
        if (raw.length > MAX_LAYOUT_PAYLOAD_CHARS) {
            reasons.push('PAYLOAD_TOO_LARGE');
            raw = null;
        } else if (raw.trim() === '') {
            reasons.push('RAW_EMPTY');
            raw = null;
        } else {
            try { raw = JSON.parse(raw); } catch (e) { reasons.push('JSON_INVALID'); raw = null; }
        }
    }
    if (raw === null || raw === undefined) {
        reasons.push('CONFIG_ABSENT');
    } else if (!isPlainObject(raw)) {
        reasons.push('CONFIG_SHAPE_INVALID');
        raw = null;
    }

    /* ---- 2. versione + migrazione ---- */
    let migration = MIGRATION_OUTCOME_V2.IDENTITY;
    if (isPlainObject(raw)) {
        if (typeof raw.version !== 'number' || !Number.isInteger(raw.version)) {
            reasons.push('VERSION_MISSING');
            migration = MIGRATION_OUTCOME_V2.UNKNOWN_VERSION;
        } else if (raw.version > DASHBOARD_LAYOUT_SCHEMA_VERSION_V2) {
            /* future schema: MAI reinterpretato. Safe fallback al default. */
            reasons.push('VERSION_FUTURE');
            migration = MIGRATION_OUTCOME_V2.UNSUPPORTED_FUTURE_VERSION;
            raw = null;
        } else if (raw.version === 1) {
            const m = migrateV1ToV2(raw, { registry });
            migration = m.outcome;
            raw = m.config;
        } else if (raw.version !== DASHBOARD_LAYOUT_SCHEMA_VERSION_V2) {
            reasons.push('VERSION_UNSUPPORTED');
            migration = MIGRATION_OUTCOME_V2.UNKNOWN_VERSION;
            raw = null;
        }
    }

    /* ---- 3. lettura difensiva di order/widgets ---- */
    const rawOrder = isPlainObject(raw) && Array.isArray(raw.order) ? raw.order : null;
    if (isPlainObject(raw) && raw.order !== undefined && raw.order !== null
        && !Array.isArray(raw.order)) {
        reasons.push('ORDER_NOT_ARRAY');
    }
    const rawWidgets = isPlainObject(raw) && isPlainObject(raw.widgets) ? raw.widgets : null;
    if (isPlainObject(raw) && raw.widgets !== undefined && raw.widgets !== null
        && !isPlainObject(raw.widgets)) {
        reasons.push('WIDGETS_NOT_OBJECT');
    }

    /* ---- 4. AUTHORIZATION: intersezione, mai unione ---- */
    const allKeys = registry.keys();
    const authorized = Array.isArray(options.authorizedKeys)
        ? allKeys.filter((k) => options.authorizedKeys.indexOf(k) !== -1)
        : allKeys.slice();
    const authorizedSet = new Set(authorized);

    /* ---- 5. ordine canonico ---- */
    const order = [];
    const seen = new Set();

    /* 5a. ordine personale: solo chiavi valide, autorizzate, non duplicate. */
    if (rawOrder) {
        rawOrder.forEach((k) => {
            if (typeof k !== 'string') { dropped.push(k); reasons.push('ORDER_KEY_NOT_STRING'); return; }
            if (!registry.has(k)) { dropped.push(k); reasons.push('WIDGET_UNKNOWN'); return; }
            if (seen.has(k)) { dropped.push(k); reasons.push('WIDGET_DUPLICATE'); return; }
            if (!authorizedSet.has(k)) { dropped.push(k); reasons.push('WIDGET_UNAUTHORIZED'); return; }
            seen.add(k);
            order.push(k);
        });
    }

    /* 5b. merge col default: widget assenti dall'ordine personale. */
    defaultKeys.forEach((k) => {
        if (seen.has(k)) return;
        if (!authorizedSet.has(k)) { dropped.push(k); reasons.push('WIDGET_UNAUTHORIZED'); return; }
        seen.add(k);
        order.push(k);
        added.push(k);
    });

    /* 5c. chiavi autorizzate note al registry ma assenti dal default (caso 14). */
    allKeys.forEach((k) => {
        if (seen.has(k)) return;
        if (!authorizedSet.has(k)) { dropped.push(k); reasons.push('WIDGET_UNAUTHORIZED'); return; }
        seen.add(k);
        order.push(k);
        added.push(k);
        reasons.push('WIDGET_NOT_IN_DEFAULT');
    });
/* ---- 6. widgets canonici + span ---- */
    const widgets = {};
    order.forEach((k) => {
        const def = defaultConfig.widgets && defaultConfig.widgets[k];
        let enabled = def && isStrictBoolean(def.enabled) ? def.enabled : true;

        const cfg = rawWidgets ? rawWidgets[k] : undefined;
        let hasRawSpan = false;
        let rawSpan;
        if (cfg !== undefined) {
            if (!isPlainObject(cfg)) {
                reasons.push('WIDGET_ENTRY_INVALID');
            } else {
                if ('enabled' in cfg) {
                    if (isStrictBoolean(cfg.enabled)) enabled = cfg.enabled;
                    else reasons.push('ENABLED_NOT_BOOLEAN');
                }
                if ('span' in cfg) { rawSpan = cfg.span; hasRawSpan = true; }
            }
        }

        const geometry = registry.geometry(k);
        const spanCapable = !!(geometry && isFiniteNumber(geometry.defaultSpan));
        const entry = { enabled };

        if (spanCapable) {
            if (hasRawSpan) {
                const ns = normalizeSpan(rawSpan, geometry);
                if (ns.reason) reasons.push(ns.reason);
                entry.span = ns.value;
            } else {
                /* nessuna preferenza esplicita -> default di prodotto, silenzioso. */
                entry.span = geometry.defaultSpan;
            }
        }

        /* position opzionale: solo widget span-capable (welcome ne viene escluso).
         * - widget NON personalizzabile (welcome) con position -> POSITION_FORBIDDEN_FOR_WIDGET;
         * - position malformata -> reason specifica (POSITION_*);
         * - position valida ma senza coordinate -> POSITION_EMPTY.
         * In nessun caso si perde il dato in silenzio: ogni scarto e' segnalato. */
        const rawPosition = isPlainObject(cfg) && Object.prototype.hasOwnProperty.call(cfg, 'position') ? cfg.position : undefined;
        if (rawPosition !== undefined) {
            if (!spanCapable) {
                reasons.push('POSITION_FORBIDDEN_FOR_WIDGET');
            } else {
                const rp = readPosition(rawPosition);
                if (!rp.ok) {
                    reasons.push(rp.reason);
                } else if (rp.kind === 'grid') {
                    if (rp.grid.column === null) {
                        reasons.push('POSITION_COLUMN_MISSING');
                    } else {
                        const row = rp.grid.row === null ? 0 : rp.grid.row;
                        const rowSpan = rp.grid.rowSpan === null ? 1 : rp.grid.rowSpan;
                        if (isFiniteNumber(entry.span) && rp.grid.column + entry.span > SPAN_ABS_MAX) {
                            reasons.push('POSITION_COLUMN_OVERFLOW');
                        } else {
                            entry.position = { row, column: rp.grid.column, rowSpan };
                        }
                    }
                } else if (rp.kind === 'legacy') {
                    if (rp.legacy.zone !== null || rp.legacy.row !== null || rp.legacy.rank !== null) {
                        entry.position = { zone: rp.legacy.zone, row: rp.legacy.row, rank: rp.legacy.rank };
                    } else {
                        reasons.push('POSITION_EMPTY');
                    }
                } else {
                    reasons.push('POSITION_EMPTY');
                }
            }
        }

        widgets[k] = entry;
    });

    return {
        config: { version: DASHBOARD_LAYOUT_SCHEMA_VERSION_V2, order, widgets },
        authorized,
        dropped,
        added,
        migration,
        reasons: Array.from(new Set(reasons)),
    };
}

export default {
    SPAN_ABS_MIN,
    SPAN_ABS_MAX,
    MIGRATION_OUTCOME_V2,
    normalizeSpan,
    serializeCanonicalV2,
    migrateV1ToV2,
    buildDefaultV2,
    normalizeDashboardLayoutV2,
    projectZonePositionToGrid,
    detectGridCollisions,
    projectLayoutZoneToGrid,
};